import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';

import EventCard from './EventCard';
import MobileEventCard from './MobileEventCard';
import { PHONE_GUTTER } from './EventTicket';
import { useIsMobile } from '../../shared/useIsMobile';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { longDate } from '../../shared/taxonomy';
import { localDate, type Opportunity } from '../../shared/opportunity';
import { isPastEvent, type Moment } from '../../shared/pastEvents';
import type { LoosenSuggestion } from './filters';

/**
 * The results list.
 *
 * Cards arrive from the API forty at a time, and more are asked for as a
 * sentinel comes within 600px of the end of what has loaded. When the list
 * opened partway down the week — to keep the reader's place across a filter
 * change — a second sentinel does the same at the top, and the pane is held
 * still while the earlier cards slide in above.
 */

/** How far ahead of the end of the loaded cards the next page is asked for. */
const PREFETCH_MARGIN = '600px 0px';

/**
 * The tag rail's width. The date is indented by this so its left edge lands on
 * the card rather than the tags — but only while the rail exists. Below the lg
 * breakpoint the rail collapses and the tags move inside the card, so the
 * indent has to go with it or the date floats off to the right of the card.
 */
export const TAG_RAIL_W = 132;
export const RAIL_BREAKPOINT = 1200;

/**
 * Sticky date, Luma-style.
 *
 * Each date pins just below the app header while its own events scroll past,
 * and the next date pushes it up and takes its place. The push falls out of
 * `position: sticky` on its own: because each header sticks within its own
 * group's box, the following group's header shoves the previous one out as its
 * box scrolls up. A single shared sticky element would swap abruptly instead.
 *
 * The weekday sits immediately right of the date, and the pair is indented to
 * the tag rail's width so the date's left edge aligns with the card, not the
 * tag.
 */
const DateHeader = styled('div')({
  position: 'sticky',
  // Sits directly under the sticky results bar above it — or at the pane's own
  // top on a phone, whose bar has moved into the header.
  top: 'var(--date-head-top, 38px)',
  // Above the pane's top fade (5), so the date is never dimmed by it.
  zIndex: 6,
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'flex-start',
  gap: 30,
  padding: '14px 0 10px',
  marginLeft: TAG_RAIL_W,
  // No opaque box: the top fade in the pane supplies the ground behind this.
  [`@media (max-width: ${RAIL_BREAKPOINT - 1}px)`]: { marginLeft: 0 },
});

/**
 * Where the reader's day is read off, in px down the pane: just under a held
 * date header (the results bar's 38, and the header's own 56). The day whose
 * cards cross the band from here to 30% of the way down is the one they are on.
 */
const FOCUS_MARGIN = '-96px 0px -70% 0px';

/** One day's header plus its cards, so the header sticks within its own group. */
const DayGroup = styled('section')({
  position: 'relative',
});

const DateBig = styled('h2')({
  margin: 0,
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 27,
  letterSpacing: '-0.01em',
  color: BRAND.parchment,
});

const DateDay = styled('span')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: 20,
  color: 'rgba(243,238,231,0.72)',
  whiteSpace: 'nowrap',
});

/**
 * A card being hidden: it fades, then its row closes so the cards below slide
 * up into its place. The inner box clips the card while the row shrinks.
 * App drops the event from the list once this has finished.
 *
 * On a phone the column is pinned to its width. A grid's implicit column grows
 * to fit its content, so a card with a long facts row came out wider than the
 * screen, which put the list on a sideways scroll and fought the swipe. It also
 * reaches out past the list's inset to the screen's edges, where a swiped card
 * and its band go, so that clipping a closing row never clips the tag notes.
 */
const Leave = styled('div', { shouldForwardProp: (p) => p !== '$leaving' })<{ $leaving: boolean }>(
  ({ theme, $leaving }) => ({
    display: 'grid',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      margin: `0 -${PHONE_GUTTER}px`,
    },
    gridTemplateRows: $leaving ? '0fr' : '1fr',
    opacity: $leaving ? 0 : 1,
    transition: $leaving
      ? 'opacity 160ms ease, grid-template-rows 280ms cubic-bezier(0.4, 0, 0.2, 1) 140ms'
      : 'none',
    '& > div': { minHeight: 0, overflow: $leaving ? 'hidden' : 'visible' },
  }),
);

const pillSx = {
  color: BRAND.parchment,
  borderColor: 'rgba(243,238,231,0.45)',
  borderRadius: 999,
  px: 2.5,
  '&:hover': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.08)' },
} as const;

interface EventListProps {
  /** What is shown, in order. */
  events: Opportunity[];
  /** Every match, for "Showing N of M" at the foot. */
  total: number | null;
  /** The first page for the current filters has arrived, so an empty list means empty. */
  ready: boolean;
  /** Bumped when a new first page lands, which is a new list rather than a longer one. */
  generation: number;
  hasMore: boolean;
  hasPrevious: boolean;
  loadMore: () => void;
  loadPrevious: () => void;
  /** Asking for another page failed. */
  moreError: boolean;
  onRetry: () => void;
  /** The scrolling pane, which the sentinels are watched against. */
  pane: RefObject<HTMLDivElement | null>;
  now: Moment;
  suggestion: LoosenSuggestion | null;
  isScheduled: (id: string) => boolean;
  isSaved: (id: string) => boolean;
  /** The list under the Saved pill: a phone swipe can move a card to the other list, and cards mark which list holds them. */
  savedView: boolean;
  onOpen: (event: Opportunity, rank: number) => void;
  onToggleSchedule: (id: string) => void;
  onToggleSave: (id: string) => void;
  onReport: (event: Opportunity) => void;
  onFilterTopic: (key: string) => void;
  isHidden: (id: string) => boolean;
  /** Cards playing their hide animation. */
  leavingIds: Set<string>;
  onToggleHidden: (id: string) => void;
  onLoosen: (s: LoosenSuggestion) => void;
  onResetAll: () => void;
  /** Bumped to demonstrate the swipe on the first card. Phones only. */
  swipeHint?: number;
  /** Told the day the reader has scrolled to, whenever it changes: the topmost one under the held date. */
  onFocusDay?: (dayKey: string) => void;
  /** Told which desktop card the pointer is over, and null when it leaves one. */
  onHover?: (event: Opportunity | null) => void;
}

export default function EventList({
  events,
  total,
  ready,
  generation,
  hasMore,
  hasPrevious,
  loadMore,
  loadPrevious,
  moreError,
  onRetry,
  pane,
  now,
  suggestion,
  isScheduled,
  isSaved,
  savedView,
  onOpen,
  onToggleSchedule,
  onToggleSave,
  onReport,
  onFilterTopic,
  isHidden,
  leavingIds,
  onToggleHidden,
  onLoosen,
  onResetAll,
  swipeHint = 0,
  onFocusDay,
  onHover,
}: EventListProps) {
  const mobile = useIsMobile();

  /**
   * The gesture, performed once on the reader's behalf.
   *
   * Swiping a card is the only way to save one on a phone, and an interaction
   * with no visible control has to be shown rather than described. The first
   * card leans right, comes back, leans left and comes back — about as long as
   * it takes to notice, and it happens exactly once per browser.
   */
  const [hintDirection, setHintDirection] = useState<'right' | 'left' | null>(null);

  useEffect(() => {
    if (!swipeHint || !mobile) return;
    const timers = [
      setTimeout(() => setHintDirection('right'), 260),
      setTimeout(() => setHintDirection(null), 900),
      setTimeout(() => setHintDirection('left'), 1150),
      setTimeout(() => setHintDirection(null), 1790),
    ];
    return () => timers.forEach(clearTimeout);
  }, [swipeHint, mobile]);

  const bottom = useRef<HTMLDivElement | null>(null);
  const top = useRef<HTMLDivElement | null>(null);

  /**
   * The observers are rebuilt whenever the list changes length, because a
   * sentinel that is still within the margin after a page lands does not
   * report a new intersection — and the next page would never be asked for.
   */
  useEffect(() => {
    const node = bottom.current;
    if (!node || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: pane.current, rootMargin: PREFETCH_MARGIN },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [events.length, hasMore, loadMore, pane]);

  useEffect(() => {
    const node = top.current;
    if (!node || !hasPrevious) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadPrevious();
      },
      { root: pane.current, rootMargin: PREFETCH_MARGIN },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [events.length, hasPrevious, loadPrevious, pane]);

  /**
   * Holding the pane still when cards arrive above.
   *
   * The first card's position is remembered as the reader scrolls; when a page
   * is added in front of it, the pane moves by however far that card was pushed
   * down. Browsers with scroll anchoring have usually done this already, in
   * which case the card has not moved and neither does the pane.
   */
  const latest = useRef(events);
  latest.current = events;
  const firstCard = useRef<{ id: string; top: number; generation: number } | null>(null);

  const measureFirst = useCallback(() => {
    const node = pane.current;
    const first = latest.current[0];
    const card = node && first
      ? node.querySelector<HTMLElement>(`[data-event-id="${CSS.escape(first.id)}"]`)
      : null;
    firstCard.current = card && first
      ? { id: first.id, top: card.getBoundingClientRect().top, generation }
      : null;
  }, [pane, generation]);

  useLayoutEffect(() => {
    const node = pane.current;
    const held = firstCard.current;
    if (node && held && held.generation === generation && events[0] && events[0].id !== held.id) {
      const card = node.querySelector<HTMLElement>(`[data-event-id="${CSS.escape(held.id)}"]`);
      if (card) {
        const delta = card.getBoundingClientRect().top - held.top;
        if (Math.abs(delta) >= 1) node.scrollTop += delta;
      }
    }
    measureFirst();
  }, [events, generation, measureFirst, pane]);

  useEffect(() => {
    const node = pane.current;
    if (!node) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measureFirst();
      });
    };
    node.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      node.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [measureFirst, pane]);

  // Group by day so headers land in the right places.
  const groups = useMemo(() => {
    const out: { dayKey: string; items: { event: Opportunity; rank: number }[] }[] = [];
    events.forEach((event, i) => {
      const day = localDate(event);
      const last = out[out.length - 1];
      if (last && last.dayKey === day) last.items.push({ event, rank: i });
      else out.push({ dayKey: day, items: [{ event, rank: i }] });
    });
    return out;
  }, [events]);

  /**
   * Which day the reader is on, for the right sidebar. Watched, never steered:
   * nothing here scrolls the pane, so it cannot fight `useListAnchor` keeping
   * the reader's place as pages arrive.
   */
  const focused = useRef<string | null>(null);
  useEffect(() => {
    const root = pane.current;
    if (!onFocusDay || !root) return;
    const crossing = new Map<Element, boolean>();
    const watch = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) crossing.set(entry.target, entry.isIntersecting);
        const day = [...root.querySelectorAll<HTMLElement>('[data-day]')].find((g) => crossing.get(g))?.dataset.day;
        if (day && day !== focused.current) {
          focused.current = day;
          onFocusDay(day);
        }
      },
      { root, rootMargin: FOCUS_MARGIN },
    );
    root.querySelectorAll('[data-day]').forEach((g) => watch.observe(g));
    return () => watch.disconnect();
  }, [groups, pane, onFocusDay]);

  if (events.length === 0) {
    if (!ready) return null;
    return (
      <Box sx={{ py: 10, textAlign: 'center', px: 3 }}>
        <Typography
          sx={{ fontFamily: FONTS.serif, fontSize: 24, fontWeight: 700, color: BRAND.parchment, mb: 1 }}
        >
          Nothing matches yet
        </Typography>

        {suggestion ? (
          <>
            <Typography sx={{ color: 'rgba(243,238,231,0.7)', fontSize: 14.5, mb: 2.5 }}>
              Drop <strong>{suggestion.label}</strong> to see {suggestion.gain}{' '}
              {suggestion.gain === 1 ? 'event' : 'events'}.
            </Typography>
            <Button variant="outlined" onClick={() => onLoosen(suggestion)} sx={pillSx}>
              Drop {suggestion.label}
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{ color: 'rgba(243,238,231,0.7)', fontSize: 14.5, mb: 2.5 }}>
              Try removing a filter or searching for something broader.
            </Typography>
            <Button variant="outlined" onClick={onResetAll} sx={pillSx}>
              Reset all filters
            </Button>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <div ref={top} aria-hidden="true" style={{ height: 1 }} />

      {groups.map((group) => {
        const { full, weekday } = longDate(group.dayKey);
        return (
          <DayGroup key={group.dayKey} data-day={group.dayKey}>
            <DateHeader>
              <DateBig>{full}</DateBig>
              <DateDay>{weekday}</DateDay>
            </DateHeader>
            {group.items.map(({ event, rank }) => (
              <Leave key={event.id} $leaving={leavingIds.has(event.id)}>
                <div
                  onMouseEnter={onHover && !mobile ? () => onHover(event) : undefined}
                  onMouseLeave={onHover && !mobile ? () => onHover(null) : undefined}
                >
                  {mobile ? (
                    <MobileEventCard
                      event={event}
                      rank={rank}
                      scheduled={isScheduled(event.id)}
                      saved={isSaved(event.id)}
                      savedView={savedView}
                      onOpen={onOpen}
                      onToggleSchedule={onToggleSchedule}
                      onToggleSave={onToggleSave}
                      // Only the first card in the list demonstrates itself.
                      hint={rank === 0 ? hintDirection : null}
                      hidden={isHidden(event.id)}
                      past={isPastEvent(event, now)}
                    />
                  ) : (
                    <EventCard
                      event={event}
                      rank={rank}
                      scheduled={isScheduled(event.id)}
                      saved={isSaved(event.id)}
                      savedView={savedView}
                      onOpen={onOpen}
                      onToggleSchedule={onToggleSchedule}
                      onToggleSave={onToggleSave}
                      onReport={onReport}
                      onFilterTopic={onFilterTopic}
                      hidden={isHidden(event.id)}
                      past={isPastEvent(event, now)}
                      onToggleHidden={onToggleHidden}
                    />
                  )}
                </div>
              </Leave>
            ))}
          </DayGroup>
        );
      })}

      <div ref={bottom} aria-hidden="true" style={{ height: 1 }} />

      {/* One card-margin of clearance, so the last card is never left sitting
          under the bottom fade — and on a phone, above the toggle's band too,
          so the lines below still read at the end of the scroll. */}
      <Box sx={{ height: 96 }} aria-hidden="true" />

      {moreError ? (
        <Typography sx={{ textAlign: 'center', py: 3, color: 'rgba(243,238,231,0.6)', fontSize: 13 }}>
          Couldn't load more events.{' '}
          <Box
            component="button"
            type="button"
            onClick={onRetry}
            sx={{
              appearance: 'none',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: BRAND.parchment,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              cursor: 'pointer',
            }}
          >
            Try again
          </Box>
        </Typography>
      ) : (
        hasMore && (
          <Typography
            sx={{ textAlign: 'center', py: 3, color: 'rgba(243,238,231,0.45)', fontSize: 13 }}
          >
            Showing {events.length} of {total ?? events.length}…
          </Typography>
        )
      )}

      <Box sx={{ height: 'var(--toggle-band, 0px)' }} aria-hidden="true" />
    </Box>
  );
}
